import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { ArrowLeft, ArrowRight, Lock } from 'lucide-react';
import { KINSEY_QUESTIONS, GENDER_OPTIONS } from '../../lib/kinsey-data';
import { KinseyQuestion } from './KinseyQuestion';
import { KinseyResults } from './KinseyResults';

const KinseyLanding = ({ onStart, onBack }) => {
  return (
    <section
      className="min-h-screen relative flex flex-col items-center justify-center px-6 overflow-hidden"
      data-testid="kinsey-landing"
    >
      <div className="relative z-10 max-w-xl mx-auto mt-12 sm:mt-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <span className="inline-block text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
            Kinsey Estimator
          </span>
        </motion.div>

        <motion.h1
          className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground leading-[1.1] mb-6 text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="kinsey-landing-headline"
        >
          Who are you actually attracted to?
        </motion.h1>

        {/* Opening text */}
        <motion.p
          className="text-base text-muted-foreground leading-relaxed text-center mb-8"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          You probably already have a sense of this.
          It's just not always something you say out loud, or fully acknowledge.
        </motion.p>

        {/* Honesty emphasis block */}
        <motion.div
          className="bg-[hsl(var(--teal)/0.04)] border border-[hsl(var(--teal)/0.15)] rounded-lg px-6 py-5 mb-6"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
          data-testid="kinsey-honesty-block"
        >
          <span className="text-xs font-semibold tracking-wide uppercase text-[hsl(var(--teal))] block mb-3">Answer honestly</span>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Don't answer as who you want to be, or what fits your current situation.
            Answer based on what's consistently true for you, even if you haven't acted on it.
          </p>
        </motion.div>

        {/* Support line */}
        <motion.p
          className="text-sm font-medium text-foreground/70 text-center mb-8"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.34, ease: [0.22, 1, 0.36, 1] }}
        >
          This works best when you're honest.
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="text-center space-y-3"
        >
          <Button
            onClick={onStart}
            data-testid="kinsey-begin-btn"
            className="rounded-full px-8 py-6 text-base font-medium bg-[hsl(var(--gold))] text-white hover:bg-[hsl(var(--gold)/0.9)] transition-transform hover:scale-[0.98] active:scale-[0.96]"
          >
            Begin
          </Button>
          <div>
            <Button
              onClick={onBack}
              variant="ghost"
              className="text-sm text-muted-foreground"
              data-testid="kinsey-landing-back"
            >
              Back to Self Discovery Hub
            </Button>
          </div>
        </motion.div>

        {/* Anchor line */}
        <motion.p
          className="text-xs text-muted-foreground/50 text-center mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Not labels. Not identity. Just a clearer picture of your pattern.
        </motion.p>

        {/* Privacy */}
        <motion.div
          className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.55 }}
        >
          <Lock className="w-3 h-3" />
          <span>Private by design. Your responses stay in your browser.</span>
        </motion.div>
      </div>
    </section>
  );
};

const GenderStep = ({ selectedGender, onSelect, onContinue, onSkip, onBack }) => {
  return (
    <section className="min-h-screen py-24 px-6" data-testid="kinsey-gender-step">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-10">
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground">
              Before we start
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-light text-foreground mb-3">
            What best describes you?
          </h2>
          <p className="text-sm text-muted-foreground mb-10">
            This is optional. It does not affect your score.
          </p>

          <div className="space-y-3" data-testid="gender-options">
            {GENDER_OPTIONS.map((option, i) => (
              <motion.div
                key={option.value}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.05 + i * 0.03 }}
              >
                <Card
                  className={`p-4 cursor-pointer transition-all border ${
                    selectedGender === option.value
                      ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.05)]'
                      : 'border-border bg-card hover:border-primary/30'
                  }`}
                  onClick={() => onSelect(option.value)}
                  data-testid={`gender-option-${option.value}`}
                >
                  <span className="text-base font-light text-foreground">{option.label}</span>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-between mt-12">
            <Button
              onClick={onBack}
              variant="ghost"
              className="rounded-full px-6"
              data-testid="gender-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex gap-3">
              <Button
                onClick={onSkip}
                variant="ghost"
                className="rounded-full px-6 text-muted-foreground"
                data-testid="gender-skip-btn"
              >
                Skip
              </Button>
              <Button
                onClick={onContinue}
                className="rounded-full px-8"
                disabled={!selectedGender}
                data-testid="gender-continue-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export const KinseyEstimator = ({ onBackToHub, onGoToHAEVN }) => {
  const [step, setStep] = useState('landing');
  const [gender, setGender] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, currentIndex]);

  const currentQuestion = KINSEY_QUESTIONS[currentIndex];
  const totalQuestions = KINSEY_QUESTIONS.length;
  const isLast = currentIndex === totalQuestions - 1;
  const selectedValue = answers[currentQuestion?.id];
  const constraintValue = answers[currentQuestion?.id + '_constraint'];

  // For constraint questions, both parts must be answered (unless partner is null)
  const hasAnswer = selectedValue !== undefined && (
    !currentQuestion?.hasConstraint || selectedValue === null || constraintValue !== undefined
  );

  const handleSelect = useCallback((value) => {
    setAnswers(prev => {
      const updated = { ...prev, [currentQuestion.id]: value };
      // Clear constraint if "no one" selected
      if (value === null) {
        delete updated[currentQuestion.id + '_constraint'];
      }
      return updated;
    });
  }, [currentQuestion]);

  const handleConstraintSelect = useCallback((value) => {
    setAnswers(prev => ({ ...prev, [currentQuestion.id + '_constraint']: value }));
  }, [currentQuestion]);

  const handleNext = useCallback(() => {
    if (isLast) setStep('results');
    else setCurrentIndex(prev => prev + 1);
  }, [isLast]);

  const handleBack = useCallback(() => {
    if (currentIndex === 0) setStep('gender');
    else setCurrentIndex(prev => prev - 1);
  }, [currentIndex]);

  if (step === 'landing') {
    return (
      <div data-testid="kinsey-estimator">
        <KinseyLanding
          onStart={() => setStep('gender')}
          onBack={onBackToHub}
        />
      </div>
    );
  }

  if (step === 'gender') {
    return (
      <div data-testid="kinsey-estimator">
        <GenderStep
          selectedGender={gender}
          onSelect={setGender}
          onContinue={() => setStep('quiz')}
          onSkip={() => { setGender('not-specified'); setStep('quiz'); }}
          onBack={() => setStep('landing')}
        />
      </div>
    );
  }

  if (step === 'results') {
    return (
      <KinseyResults
        answers={answers}
        gender={gender}
        onBackToHub={onBackToHub}
        onGoToHAEVN={onGoToHAEVN}
      />
    );
  }

  return (
    <div data-testid="kinsey-estimator">
      <KinseyQuestion
        question={currentQuestion}
        questionIndex={currentIndex}
        totalQuestions={totalQuestions}
        selectedValue={selectedValue}
        constraintValue={constraintValue}
        onSelect={handleSelect}
        onConstraintSelect={handleConstraintSelect}
      />

      <div className="fixed bottom-14 sm:bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border/40 py-4 px-6 z-40">
        <div className="max-w-2xl mx-auto flex justify-between">
          <Button
            onClick={handleBack}
            variant="ghost"
            className="rounded-full px-6"
            data-testid="kinsey-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            className="rounded-full px-8"
            disabled={!hasAnswer}
            data-testid="kinsey-next-btn"
          >
            {isLast ? 'See Results' : 'Next'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};
